import * as Sentry from '@sentry/react';

/**
 * Sentry error tracking configuration (COMP-4).
 *
 * Initialization is gated on REACT_APP_SENTRY_DSN: when the variable is not
 * set (local development, CI), `initSentry()` is a no-op and every Sentry.*
 * call throughout the app (captureException, setUser, ...) safely no-ops
 * against the uninitialized SDK.
 *
 * Scope (intentionally minimal):
 * - Error tracking only. BrowserTracing (performance monitoring) is OFF to
 *   keep bundle size and Sentry quota costs down. To enable it later:
 *
 *     Sentry.init({
 *       ...,
 *       integrations: [Sentry.browserTracingIntegration()],
 *       tracesSampleRate: 0.1, // sample 10% of transactions
 *     });
 *
 * - PII-minimal: `sendDefaultPii` is false and user enrichment is limited to
 *   the user id (see setSentryUser) — no email or name is ever attached.
 */

/** Read at call time (not module load) so tests and runtime overrides work. */
const getDsn = (): string | undefined => process.env.REACT_APP_SENTRY_DSN;

/** Whether Sentry is configured for this build/environment. */
export const isSentryEnabled = (): boolean => Boolean(getDsn());

/**
 * Initialize Sentry. Must be called as early as possible (before React
 * renders) — see src/index.tsx. No-op when REACT_APP_SENTRY_DSN is unset.
 */
export function initSentry(): void {
  const dsn = getDsn();

  if (!dsn) {
    // No DSN configured — dev/local/CI stays clean, SDK never starts.
    return;
  }

  Sentry.init({
    dsn,
    // REACT_APP_ENVIRONMENT lets deploys distinguish staging vs production
    // even though both are NODE_ENV=production builds.
    environment:
      process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV || 'development',
    release: process.env.REACT_APP_VERSION || undefined,
    // Error tracking only — no BrowserTracing integration (see module docs
    // above for how to enable performance monitoring later).
    sendDefaultPii: false,
    beforeSend(event) {
      // Defense in depth: never emit events if the DSN somehow disappears
      // (e.g., env mutation in tests or misconfigured runtime).
      if (!getDsn()) {
        return null;
      }
      return event;
    },
  });
}

/**
 * Attach the authenticated user to Sentry events.
 * PII-minimal by design: id only — never email or name.
 */
export function setSentryUser(id: string | number): void {
  Sentry.setUser({ id: String(id) });
}

/** Clear the Sentry user on logout / auth failure. */
export function clearSentryUser(): void {
  Sentry.setUser(null);
}
